import React, { useEffect, useMemo, useState } from 'react';
import './order-line-card.scss';
import {
  dispatch,
  ErrorVariants,
  getOrderStatus,
  LineDiscount,
  OrderDataLine,
  OrderLine,
  PricingStrategy,
  ProductCardVariant,
  showToast,
  useAppSelector,
  getCatalogConfig,
} from '@zonofi/zono-money-store';
import {
  OrderDiscountLevel,
  OrderProductUpdateType,
  OrderStatusEnum,
} from '../../constants';
import { SVGS } from '../../assets';
import { TouchableOpacity } from '../../common';
import {
  AddDiscount,
  LineDiscountCard,
  PromotionChips,
} from '../order-details';
import {
  getFormattedAmount,
  toMajorCurrency,
} from '@zonofi/common/functions/format-amount';
import {
  PriceInfoTooltip,
  RupeeInputField,
  SpinnerLoader,
} from '../../components';
import { QuantityEditorCard } from '../quantity-editor';
import { isOrderLineOutOfStock } from '../../helper/is-order-line-out-of-stock';

interface OrderLineCardProps {
  lineItem: OrderLine;
  isOutOfStock?: boolean;
  handleUpdate: (updatedLine: OrderLine, type: OrderProductUpdateType) => void;
  onRemovePress?: (lineItem: OrderLine) => void;
  onLineDiscountRemove?: (
    lineDiscount: LineDiscount,
    quantity: number,
    productVariantId: number
  ) => void;
  showRemoveLine?: boolean;
  onAddingLineDiscount?: (
    data: OrderDataLine,
    discountType: OrderDiscountLevel
  ) => void;
  onReplacePress?: (lineItem: OrderLine, type: ProductCardVariant) => void;
  openUomModal?: (lineItem: OrderLine) => void;
  isCart?: boolean;
  isRemoving?: boolean;
}

export const OrderLineCard: React.FC<OrderLineCardProps> = ({
  lineItem,
  isOutOfStock = false,
  handleUpdate,
  onRemovePress,
  showRemoveLine,
  onLineDiscountRemove,
  onAddingLineDiscount,
  onReplacePress,
  openUomModal,
  isCart = false,
  isRemoving,
}) => {
  const orderStatus = useAppSelector(getOrderStatus);
  const showOutOfStockTag = useAppSelector(getCatalogConfig)?.showOutOfStockTag;
  const productMOQ = useMemo(() => lineItem?.minOrderQty || 1, [lineItem]);

  const [editState, setEditState] = useState({ price: false, quantity: false });
  const [inputState, setInputState] = useState({ price: '', quantity: 0 });

  const canEditLine = useMemo(() => {
    return (
      isCart ||
      orderStatus === OrderStatusEnum.ADDING_ITEMS ||
      orderStatus === OrderStatusEnum.SUBMITTED_BY_CUSTOMER
    );
  }, [orderStatus]);

  // Show OOS only for draft orders.
  const shouldShowOutOfStock = useMemo(
    () => orderStatus === OrderStatusEnum.ADDING_ITEMS,
    [orderStatus]
  );
  const isLineOutOfStock = isOrderLineOutOfStock(
    lineItem,
    !!showOutOfStockTag && shouldShowOutOfStock
  );

  useEffect(() => {
    setInputState({
      price: toMajorCurrency(Number(lineItem?.listPrice)).toString(),
      quantity: Number(lineItem?.quantity),
    });
  }, [lineItem?.quantity, lineItem?.listPrice]);

  const onMinusPresss = () => {
    if (isLineOutOfStock) return;
    if (Number(lineItem?.quantity) - productMOQ >= productMOQ) {
      handleUpdate(
        {
          ...lineItem,
          newQnty: Number(lineItem?.quantity) - productMOQ,
        },
        OrderProductUpdateType.QUANTITY
      );
    }
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const onPlusPress = () => {
    if (isLineOutOfStock) return;
    handleUpdate(
      {
        ...lineItem,
        newQnty: Number(lineItem?.quantity) + productMOQ,
      },
      OrderProductUpdateType.QUANTITY
    );
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const onInputQuantityUpdate = () => {
    if (isLineOutOfStock) return;
    if (inputState.quantity && inputState.quantity !== lineItem?.quantity) {
      if (inputState.quantity.toString().length > 3) {
        dispatch(
          showToast({
            message: 'Product quantity should be less than 1000',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
      } else {
        handleUpdate(
          {
            ...lineItem,
            newQnty: inputState.quantity,
          },
          OrderProductUpdateType.QUANTITY
        );
      }
    }
  };

  const onInputPriceUpdate = () => {
    const originalPrice = toMajorCurrency(
      Number(lineItem?.listPrice)
    ).toString();
    if (inputState.price === originalPrice) return;

    handleUpdate(
      {
        ...lineItem,
        newPrice: Number(inputState.price),
      },
      OrderProductUpdateType.PRICE
    );
  };

  const acceptedOrderInfo = useMemo(
    () => [
      {
        id: 0,
        title: 'Price',
        value: getFormattedAmount(Math.ceil(Number(lineItem?.listPrice))),
      },
      {
        id: 1,
        title: 'Quantity',
        value: lineItem?.quantity,
      },
      {
        id: 2,
        title: 'Total',
        value: getFormattedAmount(
          (lineItem?.quantity ?? 1) * Number(lineItem?.listPrice)
        ),
      },
    ],
    [lineItem]
  );

  const price = editState.price
    ? inputState.price
    : toMajorCurrency(Number(lineItem?.listPrice)).toString();

  const isMapped = useMemo(() => !!lineItem?.productVariant.id, []);

  const getDisplayQuantity = (quantity: number) => {
    const displayQuantity = !isMapped ? 0 : quantity;
    return displayQuantity;
  };

  return (
    <div
      key={lineItem?.id}
      className="order-details-product-line-card-container"
    >
      <div
        className={`order-details-product-line-card ${canEditLine ? '' : 'product-line-card-radius'}`}
      >
        <div className="order-details-product-info">
          <div className="order-details-product-info-container">
            <div className="order-details-product-info-header">
              <span
                className={`order-details-product-info-${
                  isMapped ? 'title' : 'unmapped-title'
                } font-medium`}
              >
                {isMapped
                  ? lineItem?.productVariant?.translations?.[0]?.name
                  : lineItem?.uploadedProductName}
              </span>
              {isLineOutOfStock && (
                <span className="order-details-product-info-out-of-stock font-semibold">
                  OUT OF STOCK
                </span>
              )}
            </div>
            {!isMapped && (
              <span className="order-details-product-info-parsed-text font-semibold">{`Unmapped`}</span>
            )}

            <div className="order-details-promotions">
              {lineItem?.promotionNames &&
                lineItem?.promotionNames?.length > 0 && (
                  <PromotionChips promotions={lineItem.promotionNames} />
                )}
            </div>
            {lineItem?.lineDiscount?.discount && (
              <div className="order-details-discount-card">
                <LineDiscountCard
                  discount={Number(lineItem?.lineDiscount?.discount)}
                  linePrice={Number(lineItem?.linePrice)}
                  discountedLinePrice={Number(lineItem?.discountedLinePrice)}
                  isPercent={lineItem?.lineDiscount?.discountType === 'percent'}
                  {...(canEditLine && {
                    onRemove: () => {
                      onLineDiscountRemove?.(
                        lineItem?.lineDiscount as LineDiscount,
                        Number(lineItem?.quantity),
                        Number(lineItem?.productVariant?.id)
                      );
                    },
                  })}
                />
              </div>
            )}
          </div>

          {!canEditLine ? (
            <div className="order-details-accept-info">
              {acceptedOrderInfo.map((item) => (
                <div
                  key={item.id}
                  className="order-details-accept-info-container"
                >
                  <span className="order-details-accept-info-title font-medium">
                    {item.title}
                  </span>
                  <span className="order-details-accept-info-value font-medium">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="order-product-botttom-view">
              <div>
                <RupeeInputField
                  value={price}
                  onChange={(val) => {
                    setEditState((prev) => ({ ...prev, price: true }));
                    setInputState((prev) => ({ ...prev, price: val }));
                  }}
                  onInputBlur={() => {
                    onInputPriceUpdate();
                    setEditState((prev) => ({ ...prev, price: false }));
                  }}
                  disabled={!isMapped || isLineOutOfStock}
                />
                {lineItem?.productVariant?.pricingStrategy !==
                  PricingStrategy.STANDARD && (
                  <PriceInfoTooltip
                    title={'Computed Price'}
                    subText={
                      <>
                        quantity × price ={' '}
                        {getFormattedAmount(
                          Number(lineItem?.discountedUnitPrice)
                        )}
                      </>
                    }
                    heading={`${getFormattedAmount(Number(lineItem?.discountedUnitPrice))}`}
                  />
                )}
              </div>

              <QuantityEditorCard
                quantity={
                  editState.quantity
                    ? inputState.quantity
                    : getDisplayQuantity(Number(lineItem?.quantity))
                }
                price={
                  !isMapped
                    ? 0
                    : (editState.quantity
                        ? inputState.quantity
                        : Number(lineItem?.quantity)) *
                      Number(lineItem?.discountedUnitPrice)
                }
                onQuantityChange={(val) => {
                  setEditState((prev) => ({ ...prev, quantity: true }));
                  setInputState((prev) => ({ ...prev, quantity: val }));
                }}
                onInputBlur={() => {
                  onInputQuantityUpdate();
                  setEditState((prev) => ({ ...prev, quantity: false }));
                }}
                onMinusPress={onMinusPresss}
                onPlusPress={onPlusPress}
                disable={
                  (editState.quantity
                    ? inputState.quantity
                    : getDisplayQuantity(Number(lineItem?.quantity))) <=
                  productMOQ
                }
                disableStepper={isLineOutOfStock}
                isUnmapped={!isMapped}
                openUomModal={() => openUomModal?.(lineItem)}
                product={{
                  ...lineItem.productVariant,
                  additionalUOM:
                    lineItem?.additionalUOM ??
                    lineItem?.productVariant?.additionalUOM,
                  baseUOM:
                    lineItem?.baseUOM ?? lineItem?.productVariant?.baseUOM,
                }}
              />
            </div>
          )}
        </div>
      </div>
      {canEditLine && (
        <div className="order-details-product-line-card-actions font-semibold">
          <div className="order-details-product-line-card-actions-left">
            <div
              className="order-details-product-line-card-actions-replace"
              onClick={() =>
                onReplacePress?.(
                  lineItem,
                  lineItem?.productVariant?.id
                    ? ProductCardVariant.ORDER_REPLACE
                    : ProductCardVariant.ORDER_MAP
                )
              }
            >
              <img src={SVGS.ReplaceBlueIcon} width={20} height={20} />
              Replace
            </div>
            {isMapped && lineItem?.productVariant?.id && !isLineOutOfStock && (
              <AddDiscount
                isEdit={!!lineItem?.lineDiscount?.discount}
                onPress={() =>
                  onAddingLineDiscount?.(
                    {
                      productVariantId: Number(lineItem?.productVariant?.id),
                      price: Number(lineItem?.listPrice),
                      newQty: inputState.quantity,
                      quantity: Number(lineItem?.quantity),
                      title: lineItem.productVariant?.translations?.[0]?.name,
                      orderId: lineItem?.orderId,
                      ...(lineItem?.lineDiscount?.discount
                        ? {
                            lineDiscount: {
                              discount: lineItem.lineDiscount.discount,
                              discountType: lineItem.lineDiscount.discountType,
                            },
                          }
                        : {}),
                    },
                    'item'
                  )
                }
              />
            )}
          </div>
          {showRemoveLine &&
            (isRemoving ? (
              <SpinnerLoader
                className="order-details-spinner-loader"
                color="#dd360c"
                size={18}
              />
            ) : (
              <TouchableOpacity
                className="order-details-product-line-card-actions-delete"
                onPress={() => onRemovePress?.(lineItem)}
              >
                <img src={SVGS.DeleteIcon} width={20} height={20} />
                Remove
              </TouchableOpacity>
            ))}
        </div>
      )}
    </div>
  );
};
